import { useState, useRef, useCallback } from 'react';
import { AuthUser } from '../../../hooks/useAuth';
import { ProyectoMock } from '../../../mocks/proyectos';
import { CartaSolicitud, FirmaDigital, PLANTILLA_CARTA, MIEMBROS_COMITE } from '../../../mocks/cartas';

interface Props {
  user: AuthUser;
  proyectos: ProyectoMock[];
  cartas: CartaSolicitud[];
  onAddCarta: (carta: CartaSolicitud) => void;
  onUpdateCarta: (id: string, updates: Partial<CartaSolicitud>) => void;
}

type Vista = 'lista' | 'editor' | 'preview';
type FirmaTipo = 'texto' | 'imagen';

function generarId(): string {
  return `carta-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function formatFecha(iso: string): string {
  const d = new Date(iso);
  const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
  return `${d.getDate()} de ${meses[d.getMonth()]} de ${d.getFullYear()}`;
}

export default function CartaSolicitudEditor({ user, proyectos, cartas, onAddCarta, onUpdateCarta }: Props) {
  const misProyectos = proyectos.filter(
    p => p.email?.toLowerCase() === user.email.toLowerCase() ||
         p.investigador?.toLowerCase().includes(user.nombre.toLowerCase())
  );
  const misCartas = cartas.filter(c => c.investigadorEmail.toLowerCase() === user.email.toLowerCase());

  const [vista, setVista] = useState<Vista>('lista');
  const [editandoId, setEditandoId] = useState<string | null>(null);

  // Form state
  const [proyectoSel, setProyectoSel] = useState('');
  const [cargo, setCargo] = useState('');
  const [unidad, setUnidad] = useState('');
  const [ciudad, setCiudad] = useState('Quito');
  const [cuerpo, setCuerpo] = useState('');
  const [firmaTipo, setFirmaTipo] = useState<FirmaTipo>('texto');
  const [firmaTexto, setFirmaTexto] = useState(user.nombre);
  const [firmaImagen, setFirmaImagen] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [guardado, setGuardado] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const proyectoObj = misProyectos.find(p => p.codigo === proyectoSel);

  const iniciarNuevaCarta = useCallback(() => {
    setEditandoId(null);
    setProyectoSel(misProyectos[0]?.codigo || '');
    setCargo('');
    setUnidad('');
    setCiudad('Quito');
    const proy = misProyectos[0];
    const plantilla = PLANTILLA_CARTA
      .replace('{TITULO_PROYECTO}', proy?.titulo || '[Título del proyecto]')
      .replace('{OBJETIVO_PROYECTO}', proy?.objetivos || '[describir objetivo principal]');
    setCuerpo(plantilla);
    setFirmaTipo('texto');
    setFirmaTexto(user.nombre);
    setFirmaImagen(null);
    setEnviado(false);
    setGuardado(false);
    setVista('editor');
  }, [misProyectos, user.nombre]);

  const editarCarta = useCallback((carta: CartaSolicitud) => {
    setEditandoId(carta.id);
    setProyectoSel(carta.proyectoCodigo);
    setCargo(carta.investigadorCargo);
    setUnidad(carta.investigadorUnidad);
    setCiudad(carta.ciudad);
    setCuerpo(carta.cuerpo);
    setFirmaTipo(carta.firma?.tipo || 'texto');
    setFirmaTexto(carta.firma?.tipo === 'texto' ? carta.firma.valor : user.nombre);
    setFirmaImagen(carta.firma?.tipo === 'imagen' ? carta.firma.valor : null);
    setEnviado(false);
    setGuardado(false);
    setVista('editor');
  }, [user.nombre]);

  const handleProyectoChange = (codigo: string) => {
    setProyectoSel(codigo);
    const proy = misProyectos.find(p => p.codigo === codigo);
    const plantilla = PLANTILLA_CARTA
      .replace('{TITULO_PROYECTO}', proy?.titulo || '[Título del proyecto]')
      .replace('{OBJETIVO_PROYECTO}', proy?.objetivos || '[describir objetivo principal]');
    setCuerpo(plantilla);
  };

  const handleFirmaImagen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setFirmaImagen(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const getFirma = (): FirmaDigital | null => {
    if (firmaTipo === 'texto' && firmaTexto.trim()) return { tipo: 'texto', valor: firmaTexto.trim() };
    if (firmaTipo === 'imagen' && firmaImagen) return { tipo: 'imagen', valor: firmaImagen };
    return null;
  };

  const guardarBorrador = () => {
    if (!proyectoSel || !cuerpo.trim()) return;
    const proy = misProyectos.find(p => p.codigo === proyectoSel);
    if (!proy) return;

    if (editandoId) {
      onUpdateCarta(editandoId, {
        proyectoCodigo: proyectoSel,
        proyectoTitulo: proy.titulo,
        investigadorCargo: cargo,
        investigadorUnidad: unidad,
        ciudad,
        cuerpo,
        firma: getFirma(),
        estado: 'borrador',
      });
    } else {
      const nueva: CartaSolicitud = {
        id: generarId(),
        proyectoCodigo: proyectoSel,
        proyectoTitulo: proy.titulo,
        investigadorNombre: user.nombre,
        investigadorEmail: user.email,
        investigadorCargo: cargo,
        investigadorUnidad: unidad,
        ciudad,
        fecha: new Date().toISOString().split('T')[0],
        cuerpo,
        firma: getFirma(),
        estado: 'borrador',
        fechaEnvio: null,
        notificadoComite: false,
      };
      onAddCarta(nueva);
      setEditandoId(nueva.id);
    }
    setGuardado(true);
    setTimeout(() => setGuardado(false), 2500);
  };

  const enviarCarta = async () => {
    if (!proyectoSel || !cuerpo.trim()) return;
    const proy = misProyectos.find(p => p.codigo === proyectoSel);
    if (!proy) return;

    setEnviando(true);
    await new Promise(r => setTimeout(r, 1800));

    const ahora = new Date().toISOString();
    if (editandoId) {
      onUpdateCarta(editandoId, {
        proyectoCodigo: proyectoSel,
        proyectoTitulo: proy.titulo,
        investigadorCargo: cargo,
        investigadorUnidad: unidad,
        ciudad,
        cuerpo,
        firma: getFirma(),
        estado: 'enviada',
        fechaEnvio: ahora,
        notificadoComite: true,
      });
    } else {
      const nueva: CartaSolicitud = {
        id: generarId(),
        proyectoCodigo: proyectoSel,
        proyectoTitulo: proy.titulo,
        investigadorNombre: user.nombre,
        investigadorEmail: user.email,
        investigadorCargo: cargo,
        investigadorUnidad: unidad,
        ciudad,
        fecha: new Date().toISOString().split('T')[0],
        cuerpo,
        firma: getFirma(),
        estado: 'enviada',
        fechaEnvio: ahora,
        notificadoComite: true,
      };
      onAddCarta(nueva);
    }

    setEnviando(false);
    setEnviado(true);
  };

  const handleImprimir = () => {
    const proy = misProyectos.find(p => p.codigo === proyectoSel);
    const firma = getFirma();
    const fechaHoy = formatFecha(new Date().toISOString());

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Carta de Solicitud - ${proy?.titulo || ''}</title>
        <style>
          body { font-family: 'Times New Roman', serif; font-size: 12pt; margin: 0; padding: 0; color: #1a1a1a; }
          .page { width: 21cm; min-height: 29.7cm; margin: 0 auto; padding: 2.5cm 3cm; box-sizing: border-box; }
          .header { display: flex; align-items: center; gap: 20px; border-bottom: 2px solid #0d9488; padding-bottom: 16px; margin-bottom: 24px; }
          .header-text h1 { font-size: 14pt; font-weight: bold; margin: 0 0 2px 0; color: #0d9488; }
          .header-text p { font-size: 9pt; margin: 0; color: #555; }
          .fecha-lugar { text-align: right; margin-bottom: 24px; font-size: 11pt; }
          .destinatario { margin-bottom: 20px; font-size: 11pt; }
          .destinatario strong { display: block; }
          .asunto { margin-bottom: 20px; font-size: 11pt; }
          .asunto strong { font-weight: bold; }
          .saludo { margin-bottom: 16px; font-size: 11pt; }
          .cuerpo { font-size: 11pt; line-height: 1.8; text-align: justify; white-space: pre-wrap; margin-bottom: 32px; }
          .firma-area { margin-top: 40px; }
          .firma-linea { border-top: 1px solid #333; width: 220px; margin-bottom: 6px; }
          .firma-nombre { font-size: 11pt; font-weight: bold; }
          .firma-cargo { font-size: 10pt; color: #555; }
          .firma-img { max-height: 60px; margin-bottom: 4px; }
          .footer { margin-top: 40px; border-top: 1px solid #ccc; padding-top: 10px; font-size: 8pt; color: #888; text-align: center; }
          @media print { body { -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="header">
            <div class="header-text">
              <h1>Vicerrectorado de Investigación – UGPP</h1>
              <p>Unidad de Gestión de Proyectos y Publicaciones</p>
              <p>Comité de Investigación e Interacción</p>
            </div>
          </div>
          <div class="fecha-lugar">${ciudad || 'Quito'}, ${fechaHoy}</div>
          <div class="destinatario">
            <strong>Señores</strong>
            Comité de Investigación e Interacción<br>
            Vicerrectorado de Investigación – UGPP<br>
            Presente.-
          </div>
          <div class="asunto"><strong>ASUNTO:</strong> Solicitud de evaluación de proyecto de investigación – ${proy?.codigo || ''}</div>
          <div class="saludo">Estimados miembros del Comité:</div>
          <div class="cuerpo">${cuerpo}</div>
          <div class="firma-area">
            ${firma?.tipo === 'imagen' ? `<img src="${firma.valor}" class="firma-img" alt="Firma" />` : ''}
            <div class="firma-linea"></div>
            <div class="firma-nombre">${firma?.tipo === 'texto' ? firma.valor : user.nombre}</div>
            ${cargo ? `<div class="firma-cargo">${cargo}</div>` : ''}
            ${unidad ? `<div class="firma-cargo">${unidad}</div>` : ''}
            <div class="firma-cargo">${user.email}</div>
          </div>
          <div class="footer">
            Documento generado por el Sistema UGPP – Vicerrectorado de Investigación · ${new Date().getFullYear()}
          </div>
        </div>
      </body>
      </html>
    `;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => { win.print(); }, 500);
    }
  };

  // ── VISTA: LISTA ──────────────────────────────────────────────────────────
  if (vista === 'lista') {
    return (
      <div>
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <span className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-lg">
                <i className="ri-mail-send-line text-teal-600 text-base" />
              </span>
              Carta de Solicitud al Comité
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              Redacte y envíe su carta formal de solicitud de evaluación al Comité de Investigación e Interacción.
            </p>
          </div>
          <button
            onClick={iniciarNuevaCarta}
            disabled={misProyectos.length === 0}
            className="whitespace-nowrap flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></span>
            Nueva carta
          </button>
        </div>

        {misProyectos.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-amber-600" />
            </span>
            <p className="text-sm text-amber-700">
              Debe tener al menos un proyecto presentado para poder redactar una carta de solicitud.
              Vaya a <strong>Presentar Proyecto</strong> primero.
            </p>
          </div>
        )}

        {/* Info del comité */}
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 mb-6">
          <p className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-team-line" /></span>
            Miembros del Comité de Investigación e Interacción
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {MIEMBROS_COMITE.map((m, i) => (
              <div key={i} className="flex items-center gap-2.5 bg-white border border-teal-100 rounded-lg px-3 py-2">
                <div className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                  <i className="ri-user-line text-teal-600 text-xs" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-700 truncate">{m.nombre}</p>
                  <p className="text-xs text-slate-500 truncate">{m.cargo}</p>
                  <p className="text-xs text-teal-600 truncate">{m.email}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-teal-600 mt-3 flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-mail-check-line" /></span>
            Al enviar su carta, se notificará automáticamente a todos los miembros del Comité por correo electrónico.
          </p>
        </div>

        {/* Lista de cartas */}
        {misCartas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-full mb-4">
              <i className="ri-mail-line text-3xl text-slate-400" />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-2">Aún no tiene cartas redactadas</h3>
            <p className="text-slate-500 text-sm mb-5 max-w-sm">
              Redacte su primera carta de solicitud formal para que el Comité evalúe su proyecto.
            </p>
            {misProyectos.length > 0 && (
              <button
                onClick={iniciarNuevaCarta}
                className="whitespace-nowrap flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></span>
                Redactar primera carta
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-600 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-list-3-line" /></span>
              Mis cartas de solicitud ({misCartas.length})
            </h3>
            {misCartas.map(carta => (
              <div key={carta.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${
                      carta.estado === 'enviada' ? 'bg-teal-100' :
                      carta.estado === 'leida' ? 'bg-teal-100' : 'bg-amber-100'
                    }`}>
                      <i className={`text-lg ${
                        carta.estado === 'enviada' ? 'ri-mail-send-line text-teal-600' :
                        carta.estado === 'leida' ? 'ri-mail-check-line text-teal-600' :
                        'ri-draft-line text-amber-600'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-mono text-xs font-bold text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded">
                          {carta.proyectoCodigo}
                        </span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                          carta.estado === 'enviada' ? 'bg-teal-50 text-teal-700 border-teal-200' :
                          carta.estado === 'leida' ? 'bg-teal-50 text-teal-700 border-teal-200' :
                          'bg-amber-50 text-amber-700 border-amber-200'
                        }`}>
                          {carta.estado === 'enviada' ? 'Enviada' : carta.estado === 'leida' ? 'Leída por el Comité' : 'Borrador'}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-slate-700 leading-snug">{carta.proyectoTitulo}</p>
                      {carta.fechaEnvio && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          Enviada el {new Date(carta.fechaEnvio).toLocaleDateString('es-EC', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {carta.estado === 'borrador' && (
                      <button
                        onClick={() => editarCarta(carta)}
                        className="whitespace-nowrap flex items-center gap-1.5 text-xs font-semibold text-teal-600 border border-teal-300 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-md transition-colors cursor-pointer"
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-edit-line" /></span>
                        Editar
                      </button>
                    )}
                    <button
                      onClick={() => editarCarta(carta)}
                      className="whitespace-nowrap flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 bg-slate-50 hover:bg-slate-100 px-3 py-1.5 rounded-md transition-colors cursor-pointer"
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-eye-line" /></span>
                      Ver
                    </button>
                  </div>
                </div>
                {carta.notificadoComite && (
                  <div className="border-t border-teal-100 bg-teal-50 px-4 py-2 flex items-center gap-2">
                    <span className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-check-line text-teal-600 text-xs" />
                    </span>
                    <p className="text-xs text-teal-700">
                      Notificación enviada a {MIEMBROS_COMITE.length} miembros del Comité de Investigación e Interacción
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── VISTA: EDITOR ─────────────────────────────────────────────────────────
  const cartaActual = editandoId ? misCartas.find(c => c.id === editandoId) : null;
  const yaEnviada = cartaActual?.estado === 'enviada' || cartaActual?.estado === 'leida';

  if (enviado) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-20 h-20 flex items-center justify-center bg-teal-100 rounded-full mb-5">
          <i className="ri-mail-check-line text-4xl text-teal-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">¡Carta enviada exitosamente!</h3>
        <p className="text-slate-500 text-sm mb-2 max-w-md">
          Su carta de solicitud ha sido enviada al Comité de Investigación e Interacción.
        </p>
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-6 max-w-md w-full">
          <p className="text-sm font-semibold text-teal-800 mb-2 flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-notification-3-line" /></span>
            Notificaciones enviadas a:
          </p>
          <div className="space-y-1.5">
            {MIEMBROS_COMITE.map((m, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-teal-700">
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-check-line" /></span>
                <span className="font-medium">{m.nombre}</span>
                <span className="text-teal-500">·</span>
                <span>{m.email}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleImprimir}
            className="whitespace-nowrap flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line" /></span>
            Imprimir / Descargar
          </button>
          <button
            onClick={() => { setVista('lista'); setEnviado(false); }}
            className="whitespace-nowrap flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
            Volver a mis cartas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => setVista('lista')}
          className="whitespace-nowrap flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 cursor-pointer"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
          Mis cartas
        </button>
        <span className="text-slate-300">/</span>
        <h2 className="text-lg font-bold text-slate-800">
          {editandoId ? (yaEnviada ? 'Ver carta enviada' : 'Editar carta') : 'Nueva carta de solicitud'}
        </h2>
      </div>

      {yaEnviada && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-5 flex items-center gap-2">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-lock-line text-teal-600" />
          </span>
          <p className="text-sm text-teal-700">
            Esta carta ya fue enviada al Comité. Puede verla e imprimirla, pero no editarla.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulario izquierda */}
        <div className="lg:col-span-2 space-y-5">
          {/* Proyecto */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center bg-teal-100 rounded">
                <i className="ri-folder-line text-teal-600 text-xs" />
              </span>
              Proyecto de referencia
            </h3>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Proyecto al que corresponde la carta</label>
              <select
                value={proyectoSel}
                onChange={e => handleProyectoChange(e.target.value)}
                disabled={yaEnviada}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-400 disabled:bg-slate-50 disabled:text-slate-500 cursor-pointer"
              >
                <option value="">— Seleccione un proyecto —</option>
                {misProyectos.map(p => (
                  <option key={p.codigo} value={p.codigo}>{p.codigo} · {p.titulo}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Datos del remitente */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center bg-teal-100 rounded">
                <i className="ri-user-line text-teal-600 text-xs" />
              </span>
              Datos del remitente
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Nombre completo</label>
                <input
                  type="text"
                  value={user.nombre}
                  disabled
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-500 bg-slate-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Correo electrónico</label>
                <input
                  type="text"
                  value={user.email}
                  disabled
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-500 bg-slate-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Cargo / Título académico</label>
                <input
                  type="text"
                  value={cargo}
                  onChange={e => setCargo(e.target.value)}
                  disabled={yaEnviada}
                  placeholder="Ej: Docente Investigador, Mg., Dr."
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-400 disabled:bg-slate-50 disabled:text-slate-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Unidad / Facultad</label>
                <input
                  type="text"
                  value={unidad}
                  onChange={e => setUnidad(e.target.value)}
                  disabled={yaEnviada}
                  placeholder="Ej: Facultad de Ciencias de la Salud"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-400 disabled:bg-slate-50 disabled:text-slate-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Ciudad</label>
                <input
                  type="text"
                  value={ciudad}
                  onChange={e => setCiudad(e.target.value)}
                  disabled={yaEnviada}
                  placeholder="Quito"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-400 disabled:bg-slate-50 disabled:text-slate-500"
                />
              </div>
            </div>
          </div>

          {/* Cuerpo de la carta */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center bg-teal-100 rounded">
                  <i className="ri-file-text-line text-teal-600 text-xs" />
                </span>
                Cuerpo de la carta
              </h3>
              {!yaEnviada && (
                <span className="text-xs text-slate-400">Puede editar el texto sobre la plantilla</span>
              )}
            </div>

            {/* Encabezado visual de la carta */}
            <div className="border border-slate-200 rounded-lg overflow-hidden mb-3">
              <div className="bg-teal-700 px-4 py-3 flex items-center gap-3">
                <div>
                  <p className="text-white text-xs font-bold">Vicerrectorado de Investigación – UGPP</p>
                  <p className="text-teal-200 text-xs">Comité de Investigación e Interacción</p>
                </div>
              </div>
              <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                <div className="flex justify-between text-xs text-slate-500">
                  <span><strong>De:</strong> {user.nombre} · {user.email}</span>
                  <span>{ciudad || 'Quito'}, {new Date().toLocaleDateString('es-EC', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  <strong>Para:</strong> Comité de Investigación e Interacción
                </div>
                {proyectoObj && (
                  <div className="text-xs text-slate-500 mt-1">
                    <strong>Asunto:</strong> Solicitud de evaluación – {proyectoObj.codigo}
                  </div>
                )}
              </div>
              <div className="p-4">
                <p className="text-xs text-slate-600 mb-2 font-medium">Estimados miembros del Comité:</p>
                <textarea
                  value={cuerpo}
                  onChange={e => setCuerpo(e.target.value)}
                  disabled={yaEnviada}
                  rows={12}
                  className="w-full text-sm text-slate-700 leading-relaxed resize-none focus:outline-none bg-transparent disabled:text-slate-500"
                  placeholder="Redacte el cuerpo de su carta aquí..."
                />
              </div>
            </div>
          </div>

          {/* Firma digital */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center bg-teal-100 rounded">
                <i className="ri-pen-nib-line text-teal-600 text-xs" />
              </span>
              Firma digital
            </h3>

            {!yaEnviada && (
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setFirmaTipo('texto')}
                  className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors cursor-pointer ${
                    firmaTipo === 'texto' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
                  }`}
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-text" /></span>
                  Firma en texto
                </button>
                <button
                  onClick={() => setFirmaTipo('imagen')}
                  className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors cursor-pointer ${
                    firmaTipo === 'imagen' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
                  }`}
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-image-line" /></span>
                  Imagen de firma
                </button>
              </div>
            )}

            {firmaTipo === 'texto' ? (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Nombre para la firma</label>
                <input
                  type="text"
                  value={firmaTexto}
                  onChange={e => setFirmaTexto(e.target.value)}
                  disabled={yaEnviada}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-400 disabled:bg-slate-50 disabled:text-slate-500"
                />
                <div className="mt-3 border-t border-slate-300 pt-3 w-48">
                  <p className="font-semibold text-slate-700 text-sm" style={{ fontFamily: 'cursive' }}>{firmaTexto || user.nombre}</p>
                  {cargo && <p className="text-xs text-slate-500 mt-0.5">{cargo}</p>}
                  {unidad && <p className="text-xs text-slate-500">{unidad}</p>}
                </div>
              </div>
            ) : (
              <div>
                {!yaEnviada && (
                  <div>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFirmaImagen} />
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="whitespace-nowrap flex items-center gap-2 border border-dashed border-teal-400 text-teal-600 hover:bg-teal-50 px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer w-full justify-center"
                    >
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-2-line" /></span>
                      {firmaImagen ? 'Cambiar imagen de firma' : 'Subir imagen de firma (PNG, JPG)'}
                    </button>
                  </div>
                )}
                {firmaImagen && (
                  <div className="mt-3 border border-slate-200 rounded-lg p-3 inline-block">
                    <img src={firmaImagen} alt="Firma digital" className="max-h-16 object-contain" />
                    {cargo && <p className="text-xs text-slate-500 mt-1">{cargo}</p>}
                  </div>
                )}
                {!firmaImagen && (
                  <p className="text-xs text-slate-400 mt-2">No se ha cargado ninguna imagen de firma.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Panel derecho */}
        <div className="space-y-4">
          {/* Acciones */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 sticky top-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Acciones</h3>

            {!yaEnviada ? (
              <div className="space-y-2.5">
                <button
                  onClick={guardarBorrador}
                  disabled={!proyectoSel || !cuerpo.trim()}
                  className="whitespace-nowrap w-full flex items-center justify-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line" /></span>
                  {guardado ? '¡Guardado!' : 'Guardar borrador'}
                </button>
                <button
                  onClick={handleImprimir}
                  disabled={!proyectoSel || !cuerpo.trim()}
                  className="whitespace-nowrap w-full flex items-center justify-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line" /></span>
                  Imprimir / Descargar PDF
                </button>
                <button
                  onClick={enviarCarta}
                  disabled={!proyectoSel || !cuerpo.trim() || enviando}
                  className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={enviando ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
                  </span>
                  {enviando ? 'Enviando...' : 'Enviar al Comité'}
                </button>
              </div>
            ) : (
              <div className="space-y-2.5">
                <button
                  onClick={handleImprimir}
                  className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line" /></span>
                  Imprimir / Descargar PDF
                </button>
                <button
                  onClick={() => setVista('lista')}
                  className="whitespace-nowrap w-full flex items-center justify-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
                  Volver
                </button>
              </div>
            )}

            {/* Destinatarios */}
            <div className="mt-5 pt-4 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-mail-line" /></span>
                Se enviará a:
              </p>
              <div className="space-y-1.5">
                {MIEMBROS_COMITE.map((m, i) => (
                  <div key={i} className="text-xs text-slate-500">
                    <span className="font-medium text-slate-600">{m.nombre}</span>
                    <br />
                    <span className="text-teal-600">{m.email}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Vista previa del proyecto */}
          {proyectoObj && (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-teal-800 mb-2 flex items-center gap-1.5">
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-folder-line" /></span>
                Proyecto seleccionado
              </p>
              <p className="font-mono text-xs font-bold text-teal-700 mb-1">{proyectoObj.codigo}</p>
              <p className="text-xs text-teal-800 font-medium leading-snug">{proyectoObj.titulo}</p>
              <p className="text-xs text-teal-600 mt-1">{proyectoObj.area}</p>
              <div className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${
                proyectoObj.estado === 'Aprobado' ? 'bg-teal-100 text-teal-700 border-teal-300' :
                proyectoObj.estado === 'En evaluación' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                'bg-slate-100 text-slate-600 border-slate-300'
              }`}>
                {proyectoObj.estado}
              </div>
            </div>
          )}
        </div>
      </div>
      <div ref={printRef} className="hidden" />
    </div>
  );
}
